import React, { useState, useEffect, useCallback } from 'react';
import { useMsal } from '@azure/msal-react';
import { getApiToken } from '../authUtils';
import {
  Box, Button, Typography, Paper, CircularProgress, Alert, Chip, Stack, Switch,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField,
  Dialog, DialogTitle, DialogContent, DialogActions, MenuItem, FormControlLabel,
  Checkbox, Tooltip, IconButton, Autocomplete, Divider,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import SendIcon from '@mui/icons-material/Send';
import VisibilityIcon from '@mui/icons-material/Visibility';
import RefreshIcon from '@mui/icons-material/Refresh';
import urlconfig from '../config/urlconfig';

const API = `${urlconfig.backend_api_base_url}/api/v2/alerts`;

const EMPTY_FORM = {
  name: '',
  alertType: 'SALES_MARGIN',
  frequency: 'DAILY',
  recipients: [],
  sendHour: 9,
  sendMinute: 0,
  timeZone: 'Asia/Kolkata',
  includeInventory: false,
  includeInternal: true,
  includeSample: true,
  locationFilter: '',
  enabled: true,
};

const STATUS_STYLE = {
  SENT:          { color: '#166534', bg: '#dcfce7' },
  SKIPPED_EMPTY: { color: '#4b5563', bg: '#f3f4f6' },
  FAILED:        { color: '#991b1b', bg: '#fee2e2' },
  PENDING:       { color: '#92400e', bg: '#fef3c7' },
};

const pad = (n) => String(n).padStart(2, '0');
const fmtDateTime = (value) => (value ? new Date(value).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' }) : '—');

const AlertsManager = () => {
  const { instance, accounts } = useMsal();

  const [subscriptions, setSubscriptions] = useState([]);
  const [dispatches, setDispatches] = useState([]);
  const [metadata, setMetadata] = useState({ alertTypes: [], frequencies: [] });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const [previewHtml, setPreviewHtml] = useState(null);
  const [busyRow, setBusyRow] = useState(null);

  // Optional override for preview / send only. Scheduled runs always use the subscription's own
  // cadence — this exists so an alert can be exercised against a period that actually has data,
  // which matters on any environment whose data is a stale snapshot.
  const [testStart, setTestStart] = useState('');
  const [testEnd, setTestEnd] = useState('');
  const testRange = testStart && testEnd ? `?start=${testStart}&end=${testEnd}` : '';

  const authFetch = useCallback(async (path, options = {}) => {
    const account = accounts[0];
    if (!account) throw new Error('No active account. Please log in.');
    const accessToken = await getApiToken(instance, account);

    const response = await fetch(`${API}${path}`, {
      ...options,
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
        ...(options.headers || {}),
      },
    });

    const isHtml = (response.headers.get('content-type') || '').includes('text/html');
    const payload = isHtml ? await response.text() : await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error((payload && payload.message) || `Request failed (${response.status})`);
    }
    return payload;
  }, [instance, accounts]);

  const loadAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [meta, subs, logs] = await Promise.all([
        authFetch('/metadata'),
        authFetch('/subscriptions'),
        authFetch('/dispatches?limit=25'),
      ]);
      setMetadata(meta);
      setSubscriptions(subs);
      setDispatches(logs);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [authFetch]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const openCreate = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setDialogOpen(true);
  };

  const openEdit = (sub) => {
    setEditingId(sub.id);
    setForm({
      name: sub.name,
      alertType: sub.alertType,
      frequency: sub.frequency,
      recipients: sub.recipients || [],
      sendHour: sub.sendHour ?? 9,
      sendMinute: sub.sendMinute ?? 0,
      timeZone: sub.timeZone || 'Asia/Kolkata',
      includeInventory: !!sub.includeInventory,
      includeInternal: !!sub.includeInternal,
      includeSample: !!sub.includeSample,
      locationFilter: sub.locationFilter || '',
      enabled: sub.enabled !== false,
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) { setError('Name is required.'); return; }
    if (!form.recipients.length) { setError('Add at least one recipient.'); return; }

    setSaving(true);
    setError(null);
    try {
      await authFetch(editingId ? `/subscriptions/${editingId}` : '/subscriptions', {
        method: editingId ? 'PUT' : 'POST',
        body: JSON.stringify({ ...form, locationFilter: form.locationFilter.trim() || null }),
      });
      setDialogOpen(false);
      setNotice(editingId ? 'Alert updated.' : 'Alert created.');
      await loadAll();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (sub) => {
    setBusyRow(sub.id);
    setError(null);
    try {
      await authFetch(`/subscriptions/${sub.id}/enabled?enabled=${!sub.enabled}`, { method: 'PATCH' });
      await loadAll();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusyRow(null);
    }
  };

  const handleDelete = async (sub) => {
    setBusyRow(sub.id);
    setError(null);
    try {
      await authFetch(`/subscriptions/${sub.id}`, { method: 'DELETE' });
      setNotice(`Deleted "${sub.name}".`);
      await loadAll();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusyRow(null);
    }
  };

  const handlePreview = async (sub) => {
    setBusyRow(sub.id);
    setError(null);
    try {
      setPreviewHtml(await authFetch(`/subscriptions/${sub.id}/preview${testRange}`));
    } catch (err) {
      setError(err.message);
    } finally {
      setBusyRow(null);
    }
  };

  const handleSendNow = async (sub) => {
    setBusyRow(sub.id);
    setError(null);
    setNotice(null);
    try {
      const result = await authFetch(`/subscriptions/${sub.id}/send${testRange}`, { method: 'POST' });
      if (result.status === 'FAILED') setError(result.message);
      else setNotice(result.message);
      await loadAll();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusyRow(null);
    }
  };

  const labelFor = (list, value) => (list.find((o) => o.value === value) || {}).label || value;

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
        <Box>
          <Typography variant="h6" fontWeight={700}>Alerts</Typography>
          <Typography variant="body2" color="text.secondary">
            Scheduled email digests for sales &amp; margin achievement and delivery status.
            Each alert reports on the previous completed period.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button startIcon={<RefreshIcon />} onClick={loadAll} disabled={loading}>Refresh</Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>New Alert</Button>
        </Stack>
      </Stack>

      {error && <Alert severity="error" sx={{ my: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {notice && <Alert severity="success" sx={{ my: 2 }} onClose={() => setNotice(null)}>{notice}</Alert>}

      {/* Preview/send override. Does not affect scheduled runs. */}
      <Paper variant="outlined" sx={{ mt: 2, p: 1.5, borderRadius: 2, bgcolor: '#f8fafc' }}>
        <Stack direction="row" alignItems="center" spacing={1.5} flexWrap="wrap" useFlexGap>
          <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary' }}>
            TEST OVER A SPECIFIC PERIOD
          </Typography>
          <TextField
            type="date" size="small" label="From" InputLabelProps={{ shrink: true }}
            value={testStart} onChange={(e) => setTestStart(e.target.value)}
            sx={{ bgcolor: '#fff', width: 170 }}
          />
          <TextField
            type="date" size="small" label="To" InputLabelProps={{ shrink: true }}
            value={testEnd} onChange={(e) => setTestEnd(e.target.value)}
            sx={{ bgcolor: '#fff', width: 170 }}
          />
          {(testStart || testEnd) && (
            <Button size="small" onClick={() => { setTestStart(''); setTestEnd(''); }}>Clear</Button>
          )}
          <Box sx={{ flexGrow: 1 }} />
          <Typography variant="caption" color={testRange ? 'primary.main' : 'text.secondary'}>
            {testRange
              ? 'Preview and Send will use this range instead of the alert\'s own period.'
              : 'Optional. Leave blank to use each alert\'s normal period. Scheduled runs are never affected.'}
          </Typography>
        </Stack>
      </Paper>

      {loading && !subscriptions.length ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
      ) : (
        <TableContainer component={Paper} variant="outlined" sx={{ mt: 2, borderRadius: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: '#f8fafc' }}>
                <TableCell sx={{ fontWeight: 700 }}>Alert</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Type</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Cadence</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Recipients</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Next Covers</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Last Sent</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="center">On</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {subscriptions.map((sub) => (
                <TableRow key={sub.id} hover>
                  <TableCell sx={{ fontWeight: 600 }}>{sub.name}</TableCell>
                  <TableCell>{labelFor(metadata.alertTypes, sub.alertType)}</TableCell>
                  <TableCell>
                    {labelFor(metadata.frequencies, sub.frequency)}
                    <Typography variant="caption" display="block" color="text.secondary">
                      {pad(sub.sendHour)}:{pad(sub.sendMinute)} {sub.timeZone}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                      {(sub.recipients || []).map((r) => <Chip key={r} label={r} size="small" />)}
                    </Stack>
                  </TableCell>
                  <TableCell>{sub.nextPeriodLabel || '—'}</TableCell>
                  <TableCell>{fmtDateTime(sub.lastDispatchedAt)}</TableCell>
                  <TableCell align="center">
                    <Switch
                      size="small"
                      checked={!!sub.enabled}
                      disabled={busyRow === sub.id}
                      onChange={() => handleToggle(sub)}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                      <Tooltip title={testRange
                        ? `Preview over ${testStart} → ${testEnd}`
                        : 'Preview the email without sending'}>
                        <span>
                          <IconButton size="small" disabled={busyRow === sub.id} onClick={() => handlePreview(sub)}>
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title={testRange
                        ? `Send ${testStart} → ${testEnd} to ${(sub.recipients || []).join(', ')}`
                        : `Send now to ${(sub.recipients || []).join(', ')}`}>
                        <span>
                          <IconButton size="small" color="primary" disabled={busyRow === sub.id} onClick={() => handleSendNow(sub)}>
                            <SendIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="Edit">
                        <span>
                          <IconButton size="small" disabled={busyRow === sub.id} onClick={() => openEdit(sub)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <span>
                          <IconButton size="small" color="error" disabled={busyRow === sub.id} onClick={() => handleDelete(sub)}>
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
              {!subscriptions.length && (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                    No alerts configured yet.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* ─── Recent dispatch history ─────────────────────────────────────────── */}
      <Typography variant="subtitle1" fontWeight={700} sx={{ mt: 4, mb: 1 }}>Recent Dispatches</Typography>
      <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: '#f8fafc' }}>
              <TableCell sx={{ fontWeight: 700 }}>When</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Type</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Period</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Subject</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {dispatches.map((log) => {
              const style = STATUS_STYLE[log.status] || STATUS_STYLE.PENDING;
              return (
                <TableRow key={log.id} hover>
                  <TableCell>{fmtDateTime(log.sentAt || log.createdAt)}</TableCell>
                  <TableCell>{labelFor(metadata.alertTypes, log.alertType)}</TableCell>
                  <TableCell>{log.periodStart} → {log.periodEnd}</TableCell>
                  <TableCell>
                    <Chip
                      label={log.status}
                      size="small"
                      sx={{ color: style.color, bgcolor: style.bg, fontWeight: 700 }}
                    />
                  </TableCell>
                  <TableCell sx={{ maxWidth: 380 }}>
                    <Typography variant="body2" noWrap title={log.errorMessage || log.subject}>
                      {log.errorMessage || log.subject || '—'}
                    </Typography>
                  </TableCell>
                </TableRow>
              );
            })}
            {!dispatches.length && (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 3, color: 'text.secondary' }}>
                  Nothing dispatched yet.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* ─── Create / edit dialog ────────────────────────────────────────────── */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>{editingId ? 'Edit Alert' : 'New Alert'}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2.5} sx={{ mt: 1 }}>
            <TextField
              label="Name" fullWidth size="small" value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              helperText="Shown in the dispatch history. Must be unique."
            />
            <Stack direction="row" spacing={2}>
              <TextField
                select label="Alert Type" fullWidth size="small" value={form.alertType}
                onChange={(e) => setForm({ ...form, alertType: e.target.value })}
              >
                {metadata.alertTypes.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
              </TextField>
              <TextField
                select label="Frequency" fullWidth size="small" value={form.frequency}
                onChange={(e) => setForm({ ...form, frequency: e.target.value })}
              >
                {metadata.frequencies.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
              </TextField>
            </Stack>

            <Autocomplete
              multiple freeSolo options={[]} value={form.recipients}
              onChange={(e, value) => setForm({ ...form, recipients: value.map((v) => v.trim()).filter(Boolean) })}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => <Chip label={option} size="small" {...getTagProps({ index })} key={option} />)
              }
              renderInput={(params) => (
                <TextField
                  {...params} label="Recipients" size="small"
                  helperText="Type an email address and press Enter."
                />
              )}
            />

            <Stack direction="row" spacing={2}>
              <TextField
                label="Send Hour" type="number" size="small" fullWidth
                inputProps={{ min: 0, max: 23 }} value={form.sendHour}
                onChange={(e) => setForm({ ...form, sendHour: Number(e.target.value) })}
              />
              <TextField
                label="Send Minute" type="number" size="small" fullWidth
                inputProps={{ min: 0, max: 59 }} value={form.sendMinute}
                onChange={(e) => setForm({ ...form, sendMinute: Number(e.target.value) })}
              />
              <TextField
                label="Time Zone" size="small" fullWidth value={form.timeZone}
                onChange={(e) => setForm({ ...form, timeZone: e.target.value })}
              />
            </Stack>

            <TextField
              label="Location Filter (optional)" size="small" fullWidth value={form.locationFilter}
              onChange={(e) => setForm({ ...form, locationFilter: e.target.value })}
              helperText="Restrict to a single cost-sheet location. Leave blank for all."
            />

            <Divider />
            <Box>
              <Typography variant="caption" display="block" color="text.secondary" sx={{ mb: 0.5 }}>
                Defaults match the Analytics Overview tab: Inventory excluded, Internal and Sample
                included. Whatever you exclude is still reported as a separate line in the email
                rather than dropped silently.
              </Typography>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={form.includeInventory}
                    onChange={(e) => setForm({ ...form, includeInventory: e.target.checked })}
                  />
                }
                label="Include inventory cost sheets"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={form.includeInternal}
                    onChange={(e) => setForm({ ...form, includeInternal: e.target.checked })}
                  />
                }
                label="Include internal cost sheets"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={form.includeSample}
                    onChange={(e) => setForm({ ...form, includeSample: e.target.checked })}
                  />
                }
                label="Include sample cost sheets"
              />
              <FormControlLabel
                sx={{ mt: 1 }}
                control={
                  <Switch
                    checked={form.enabled}
                    onChange={(e) => setForm({ ...form, enabled: e.target.checked })}
                  />
                }
                label="Enabled"
              />
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained" onClick={handleSave} disabled={saving}
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ─── Email preview ───────────────────────────────────────────────────── */}
      <Dialog open={!!previewHtml} onClose={() => setPreviewHtml(null)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Email Preview</DialogTitle>
        <DialogContent dividers sx={{ p: 0, height: '70vh' }}>
          {/* Sandboxed iframe: the alert body is generated HTML and must not run in the app's origin. */}
          <iframe
            title="Alert email preview"
            sandbox=""
            srcDoc={previewHtml || ''}
            style={{ width: '100%', height: '100%', border: 'none' }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setPreviewHtml(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AlertsManager;
